// v78 — Creator 360 profile wording. Every grade comes from the sheet; this file only words it.
import { short } from "@/lib/creator360";

export const VIEWS = ["best", "yt", "live", "tg", "ig"];
export const VIEW_TAB = { best: "Best avenues", yt: "YouTube standard videos", live: "YouTube live streams", tg: "Telegram", ig: "Instagram" };
export const VIEW_LABEL = { best: "Creator 360 · Best avenues", yt: "YouTube creator profile", live: "Live streamer profile", ig: "Instagram profile", tg: "Telegram profile" };
export const GRADE = {
  strong: { word: "Strong", color: "#39FF14" }, good: { word: "Good", color: "#FBBF24" }, weak: { word: "Weak", color: "#F43F5E" },
  not_judged: { word: "Not judged", color: "#71717A" }, not_counted: { word: "Not counted", color: "#71717A" },
};
const DELIV = { yt: "YouTube video deliverables", live: "live stream deliverables", ig: "Instagram deliverables", tg: "Telegram posts" };
const P = { yt: "YouTube", live: "live streams", ig: "Instagram", tg: "Telegram" };
const THEIR = { yt: "Their YouTube shows", live: "Their live streams show", ig: "Their Instagram shows", tg: "Their Telegram shows" };
const ISNT = { yt: "YouTube isn't", live: "Live streaming isn't", ig: "Instagram isn't", tg: "Telegram isn't" };
export const TAG = { strong: "A good avenue for deliverables", good: "Worth a deeper look", weak: "Not their strong avenue right now" };
export const ORDER = { yt: ["views", "eng", "vpw", "last"], live: ["viewers", "lpw", "chat"], ig: ["followers", "reels", "reach", "eng"], tg: ["size", "ppw", "views", "react"] };

export const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);
export const join = (xs) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);
export const comma = (n) => (n == null ? "—" : Math.round(n).toLocaleString("en-US"));
export const pct = (x) => (x == null ? "—" : `${x >= 10 ? x.toFixed(1) : x.toFixed(2)}%`);
export const num1 = (x) => (x == null ? "—" : String(Math.round(x * 10) / 10));
export function mins(m) {
  if (!m) return "—";
  const h = Math.floor(m / 60);
  const mm = Math.round(m % 60);
  return h ? `${h}h ${String(mm).padStart(2, "0")}m` : `${mm}m`;
}
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const day = (iso) => { const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${Number(m[3])} ${MON[Number(m[2]) - 1]} ${m[1]}` : null; };
export function daysAgo(iso, today) {
  if (!iso) return null;
  return Math.max(0, Math.round((new Date(today) - new Date(iso.slice(0, 10))) / 864e5));
}
export function agoWords(d) {
  if (d == null) return null;
  if (d === 0) return "today";
  if (d === 1) return "yesterday";
  if (d <= 7) return d <= 6 ? `${d} days ago` : "this week";
  if (d <= 14) return "over a week ago";
  if (d <= 30) return "over 2 weeks ago";
  return "over a month ago";
}

// ---- per-check phrases (Strong / Good / Weak), all neutral pronouns --------------------------
function phrase(av, k, g, d) {
  const n = d.nums[av];
  const T = {
    yt: {
      views: ["their videos get more views for their size than usual", "their views are a little below usual for their size", "their views are low for their size"],
      eng: ["their viewers engage more than usual", "their engagement is a little below usual", "their engagement is low for their size"],
      vpw: ["they post standard videos often", "they post standard videos a bit less often than most", "they rarely post standard videos"],
      last: ["they posted a standard video recently", "their last standard video was over 10 days ago",
        d.av.yt.checks.last?.word === "m1" ? "they have not posted a standard video for over a month"
          : d.av.yt.checks.last?.word === "none" ? "none of their newest uploads is a standard video" : "their last standard video was over 2 weeks ago"],
    },
    live: {
      viewers: ["they pull a big live crowd", "their live crowd is decent but not big yet", "their live crowd is still small"],
      lpw: ["they go live often", "they go live only once to three times a week", "they go live less than once a week"],
      chat: ["their live chat is busy", "their live chat is moderate", "their live chat is quiet"],
    },
    ig: {
      followers: [(n.of_yt ?? 0) >= 30 ? "their Instagram is potentially a material part of their audience" : "their Instagram audience is big",
        "their Instagram is a smaller audience than their YouTube", "their account is still small"],
      reels: ["they post reels regularly", "they post reels now and then", "they rarely post reels"],
      reach: ["their reels reach more of their followers than usual for their size", "their reels reach a bit fewer of their followers than usual for their size",
        `their reels reach ${d.av.ig.r === "good" ? "fewer" : "far fewer"} of their followers than usual for their size`],
      eng: ["people engage more than usual for their size", "their engagement is a bit lower than usual for their size", "their engagement is low for their size"],
    },
    tg: {
      size: [(n.of_yt ?? 0) >= 10 ? "their Telegram is potentially a material part of their audience" : "their Telegram audience is big",
        "their Telegram is a smaller audience than their YouTube", "their channel is still small"],
      ppw: ["they post regularly", "they post now and then", (n.last_days ?? 0) > 7 ? "they have not posted for over 2 weeks" : "they post only now and then"],
      views: ["their subscribers really read their posts", "a bit fewer of their subscribers see each post than usual for their size",
        `${d.av.tg.r === "good" ? "fewer" : "far fewer"} of their subscribers see each post than usual for their size`],
      react: ["their posts get plenty of reactions", "their posts get a bit fewer reactions than usual for their size", "their posts get few reactions for their size"],
    },
  };
  const i = { strong: 0, good: 1, weak: 2 }[g];
  return i == null ? null : T[av][k]?.[i];
}

const BOT_WORD = { medium: "Medium", high: "High" };
const BOT_PLAT = { yt: "YouTube", live: "live", ig: "Instagram", tg: "Telegram" };

function phrasesBy(av, d) {
  const x = d.av[av];
  const by = { strong: [], good: [], weak: [] };
  for (const k of ORDER[av]) {
    const g = x.checks[k]?.grade;
    const p = phrase(av, k, g, d);
    if (p) by[g].push(p);
  }
  if (x.bot) by.weak.push(`their ${BOT_PLAT[av]} bot check shows ${BOT_WORD[x.bot]}`);
  return by;
}

function keepLine(av, d) {
  const x = d.av[av];
  const n = d.nums[av];
  if (av === "yt" && x.tail?.kind === "keep") return "their views and engagement are both strong";
  if (x.tail?.kind !== "min_reach") return null;
  return av === "ig" ? `a typical reel still reaches about ${short(n.reel_views)} people` : `each post still reaches about ${short(n.avg_views)} people`;
}

export function unknownNote(av, d) {
  const x = d.av[av];
  const n = d.nums[av];
  if (av === "yt") return "No YouTube numbers for them yet.";
  if (av === "live") return /does not/i.test(x.label) || d.nums.live.group === "never" ? "They do not go live." : "No live stream has been measured for them yet.";
  if (av === "ig") return n.followers ? "Their follower count is known, but their reels could not be read yet, so there is no verdict." : "No Instagram account found for them yet.";
  return d.creator.tg && n.subs ? "Their Telegram is found, but its posts could not be read yet." : "No Telegram channel found for them yet.";
}

export const tgUnread = (d) => d.av.tg.r === "good" && /activity unknown/i.test(d.av.tg.label);
const TG_UNREAD = "Their Telegram posts can't be read (the channel preview is turned off), so only the size of their channel is known.";

// The verdict box reason, built only from the sheet's grades.
export function reason(av, d) {
  const x = d.av[av];
  if (x.r === "unknown") return unknownNote(av, d);
  if (av === "tg" && tgUnread(d)) return `${TG_UNREAD} Worth a deeper look by opening the channel itself.`;
  const by = phrasesBy(av, d);
  const worth = `Worth a deeper look: ${DELIV[av]} could work as a secondary source of reach alongside their primary deliverables.`;
  if (x.r === "good" && x.tail?.kind === "although") return `${THEIR[av]} promise. ${cap(x.tail.text)}. ${worth}`;
  if (x.r === "weak" && x.tail?.kind === "weak_only") return `Weak on ${P[av]} only because ${x.tail.cause}. ${cap(x.tail.rest)}.`;
  if (x.r === "strong") return `Strong on ${P[av]}: ${join(by.strong.length ? by.strong : [...by.good, ...by.weak])}. A good avenue for ${DELIV[av]}.`;
  if (x.r === "good") {
    const low = [...by.good, ...by.weak];
    const keep = keepLine(av, d);
    const body = low.length ? `${cap(join(low))}${by.strong.length ? `, but ${join(by.strong)}` : ""}.` : `${cap(join(by.strong))}.`;
    return `${THEIR[av]} promise. ${body}${keep ? ` ${cap(keep)}.` : ""} ${worth}`;
  }
  const weak = by.weak.length ? by.weak : by.good;
  return `${ISNT[av]} their strong avenue right now${weak.length ? `: ${join(weak)}` : ""}. Better to focus deliverables on their stronger avenues.`;
}

// The one plain "why" line on a Best avenues card.
export function whyLine(av, d) {
  const x = d.av[av];
  if (x.r === "unknown") return unknownNote(av, d);
  if (av === "tg" && tgUnread(d)) return `Good: their posts can't be read (the channel preview is turned off), so only the size of their channel is known.`;
  if (x.r === "good" && x.tail?.kind === "although") return `Good: ${x.tail.text}.`;
  if (x.r === "weak" && x.tail?.kind === "weak_only") return `Weak only because ${x.tail.cause}, so worth a closer look.`;
  const by = phrasesBy(av, d);
  if (x.r === "strong") return `Strong because ${join(by.strong.length ? by.strong : [...by.good, ...by.weak])}.`;
  if (x.r === "good") {
    const low = [...by.good, ...by.weak];
    return low.length ? `Good, not Strong, because ${join(low)}.${by.strong.length ? ` On the plus side, ${join(by.strong)}.` : ""}` : `Good because ${join(by.strong)}.`;
  }
  const weak = by.weak.length ? by.weak : by.good;
  return weak.length ? `Weak because ${join(weak)}.` : "Weak right now.";
}

const RANKV = { strong: 3, good: 2, weak: 1, unknown: 0 };
// A BD's own verdict (v79) orders the avenues for them.
const effOf = (a, d) => d.mine?.[a]?.r || d.av[a].r;
export function rankedAvenues(d) {
  return ["yt", "live", "tg", "ig"].slice().sort((a, b) => (RANKV[effOf(b, d)] - RANKV[effOf(a, d)]) || ((d.av[b].n || 0) - (d.av[a].n || 0)));
}
export function rankLine(av, d) {
  if (effOf(av, d) === "unknown") return "Not enough data to judge";
  const known = rankedAvenues(d).filter((a) => effOf(a, d) !== "unknown");
  const i = known.indexOf(av);
  return i === 0 ? "Their strongest avenue" : `No. ${i + 1} of their ${known.length} avenues`;
}

// Lives a week: the merged list's bigger count wins only when it gives the same grade.
const lpwGrade = (w) => (w >= 3 ? "strong" : w >= 1 ? "good" : "weak");
export function livesWeek(d) {
  const n = d.nums.live;
  const big = d.live.n28_15;
  if (n.lives4 == null) return { v: null, group: n.group };
  if (big > n.lives4 && lpwGrade(big / 4) === lpwGrade(n.lives4 / 4)) return { v: big / 4, count: big, merged: true };
  return { v: n.lpw, count: n.lives4 };
}

export function liveLast(d) {
  if (d.live.live_now) return "Live now";
  const w = agoWords(d.live.last_days);
  return w ? `Last live ${w}` : null;
}

export function streamingPill(d) {
  const l4 = d.nums.live.lives4;
  if (l4 != null && l4 > 0) return l4 >= 12 ? "Frequent live streamer" : l4 >= 4 ? "Regular live streamer" : "Rare live streamer";
  if (l4 == null) return { frequent: "Frequent live streamer", regular: "Regular live streamer", rare: "Rare live streamer" }[d.nums.live.group] || null;
  return null;
}
